import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { contractsApi, type ContractSub, type ContractOwner, type ContractsDashboard } from '@/lib/apiClient';
import { Button, Card, StateView } from '@/components';
import ContractsStyles from './ContractsStyles';
import ContractsBoard from './ContractsBoard';
import SubcontractModal from './SubcontractModal';
import OwnerContractModal from './OwnerContractModal';
import SubcontractRequestQueue from './SubcontractRequestQueue';
import { countSubs, countOpenOwners } from './contractsModel';

function Kpi({ label, value, warn }: { label: string; value: number; warn?: boolean }) {
  return (
    <Card>
      <div className="ct-kpi-label">{label}</div>
      <div className={`ct-kpi-value${warn ? ' ct-kpi-value--warn' : ''}`}>{value}</div>
    </Card>
  );
}

function Summary({ data }: { data: ContractsDashboard }) {
  const c = countSubs(data.subcontracts);
  return (
    <div className="ct-kpis">
      <Kpi label="In progress" value={c.inProgress} />
      <Kpi label="On hold" value={c.onHold} />
      <Kpi label="Can’t report to site" value={c.missingItems} warn={c.missingItems > 0} />
      <Kpi label="Cleared to report" value={c.cleared} />
      <Kpi label="Owner contracts open" value={countOpenOwners(data.ownerContracts)} />
    </div>
  );
}

/**
 * Contracts (#501): the weekly Subcontract Status Report, live. Every subcontract
 * by where it stands and which forms have come in, and the owner contracts under
 * review. Anyone with contracts.view can read it; contracts.edit adds the buttons.
 *
 * What a viewer sees follows the ordinary job-visibility rule, so the API says
 * which scope they got and the screen repeats it rather than letting a partial
 * list pass for the whole company.
 */
export default function ContractsPage() {
  const [search, setSearch] = useState('');
  const [history, setHistory] = useState(false);
  const [subModal, setSubModal] = useState<{ row?: ContractSub } | null>(null);
  const [ownerModal, setOwnerModal] = useState<{ row?: ContractOwner } | null>(null);

  const q = useQuery({
    queryKey: ['contracts', 'dashboard', history],
    queryFn: () => contractsApi.dashboard(history),
    staleTime: 30_000,
  });
  const canEdit = q.data?.access.canEdit ?? false;

  return (
    <>
      <ContractsStyles />

      <div className="ct-toolbar">
        <h1>Contracts</h1>
        <input
          type="search"
          placeholder="Search job, vendor, subcontract #"
          aria-label="Search contracts"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label>
          <input type="checkbox" checked={history} onChange={(e) => setHistory(e.target.checked)} />
          Show older (over 30 days)
        </label>
        {canEdit && (
          <>
            <Button variant="secondary" onClick={() => setOwnerModal({})}>+ Owner contract</Button>
            <Button variant="primary" onClick={() => setSubModal({})}>+ Subcontract</Button>
          </>
        )}
      </div>

      <StateView query={q}>
        {(data) => (
          <>
            {data.scope === 'yours' && (
              <div className="ct-hint" role="note">
                Showing subcontracts on the jobs you can see. MSAs and owner contracts with no job are shown to everyone with access.
              </div>
            )}
            {/* The coordinator's queue of requests from project managers (#627). */}
            {canEdit && <SubcontractRequestQueue search={search} />}
            <Summary data={data} />
            <ContractsBoard
              subcontracts={data.subcontracts}
              ownerContracts={data.ownerContracts}
              search={search}
              canEdit={canEdit}
              showJob
              onEditSub={(row) => setSubModal({ row })}
              onEditOwner={(row) => setOwnerModal({ row })}
            />
          </>
        )}
      </StateView>

      {subModal && <SubcontractModal row={subModal.row} onClose={() => setSubModal(null)} />}
      {ownerModal && <OwnerContractModal row={ownerModal.row} onClose={() => setOwnerModal(null)} />}
    </>
  );
}
